import { Stars } from "assets/icons/Stars";
import { ChatWidget } from "components/ChatWidget/ChatWidget";
import { LoadingSpinner } from "components/LoadingSpinner";
import { UserRole } from "constants/userRole";
import { feeRateSourceFromEffectiveDate } from "helpers/date";
import { centsToDollars, parsePrice } from "helpers/price";
import { useCurrentUser } from "hooks/useCurrentUser";
import { Label } from "layout/typography/Label";
import { debounce } from "lodash";
import { HorizontalLine } from "pages/Dashboard/components/styled";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { Paginator, PaginatorPageState } from "primereact/paginator";
import { Tooltip } from "primereact/tooltip";
import { useEffect, useMemo, useRef, useState } from "react";
import { useGetAccountByIdQuery } from "store/queries/account";
import { useListModelsQuery } from "store/queries/chat";
import { useGetContractInsightsRecommendationsMutation } from "store/queries/contractInsights";
import { useGetAvailableCompetitors } from "store/queries/marketPrices";
import {
  useGetTotalAllowableAndPatientPortionQuery,
  useGetTopCaseRatesWithPayersQuery,
} from "store/queries/providerPayout";
import {
  IProviderProcedureComparisonRequest,
  useGetAllProviderProcedureComparisonQuery,
  useLazyGetAllProviderProcedureComparisonQuery,
  useGetTop10ProviderProcedureComparisonQuery,
} from "store/queries/providerProcedure";
import styled from "styled-components";
import { IPayer, IPayerWithProcedurePrice } from "types/Payers/Payers";
import { SpecialtyListDict } from "types/Provider/Provider";

import { AllowableVsPtResponsibility } from "../AllowableVsPtResponsibility";
import { ContractModelingAnalysis } from "../ContractModelingAnalysis/ContractModelingAnalysis";
import { MarketPriceAnalysis } from "../MarketPriceAnalysis";
import { TopCasesWithPayers } from "../TopCasesWithPayers";
import { Insights } from "./components/Insights";
import { exportToExcel, matchModeOptions } from "./helpers";

const StyledTopCPTCodesTag = styled.div<{ active?: boolean }>`
  border: 1px solid
    ${(props) => (props.active ? `var(--primary-color)` : "#D0D5DD")};
  padding: 9px 14px;
  border-radius: 22px;
  color: ${(props) =>
    props.active ? `var(--primary-color)` : "var(--gray-new-500)"};
  font-weight: 600;
  cursor: pointer;
  font-size: 14px;
`;

const StyledContainer = styled.div`
  border-radius: 8px;
  border: 1px solid var(--gray-new-200);
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledInputText = styled(InputText)`
  border: none;
  :enabled:focus {
    border: none !important;
    box-shadow: none !important;
  }
`;

const StyledInputTextContainer = styled.div`
  border: 1px solid #ced4da;
  border-radius: 6px;
  padding-left: 0.75rem;
  display: flex;
  align-items: center;
`;

const StyledDataTable = styled(DataTable)<{ volumeFilterActive?: boolean }>`
  ${(props) =>
    props.volumeFilterActive &&
    `
    .p-datatable-thead th:nth-child(3) .p-column-filter-menu-button {
      background-color: var(--primary-300) !important;
      color: white !important;
    }
  `}
`;

export function FeeScheduleComparison({ accountId }: { accountId: string }) {
  const { data: accountResponse } = useGetAccountByIdQuery(accountId);
  const accountInfo = accountResponse?.data;
  const { data: chatModels } = useListModelsQuery();
  const { userInfo } = useCurrentUser({ skip: false });
  const [inputValues, setInputValues] = useState<{ [key: string]: any }>({}); // Store input values
  const [topResultsAmount, setTopResultsAmount] = useState(10);
  const [isDownloading, setIsDownloading] = useState(false);
  const [volumeFilter, setVolumeFilter] = useState<number | null>(null);
  const [matchMode, setMatchMode] = useState<string>("equals");
  const [filters, setFilters] = useState<IProviderProcedureComparisonRequest>({
    payerNameField: undefined,
    cptCodeField: undefined,
    cptDescription: undefined,
    specialty: "FACILITY",
    providerName: undefined,
    onlyLowerThanMedicare: false,
    volume: undefined,
    volumeMatchMode: "equals",
  });
  const [first, setFirst] = useState(0);
  const [rows] = useState(10);
  const [
    firstAllowableVsPtResponsibility,
    setFirstAllowableVsPtResponsibility,
  ] = useState(0);
  const [rowsAllowableVsPtResponsibility] = useState(25);
  const [rowsTopCasesWithPayers] = useState(25);
  const [selectedPayers, setSelectedPayers] = useState<IPayer[]>([]);
  const hasSetDefaultPayers = useRef(false);
  const [triggerDownloadQuery] =
    useLazyGetAllProviderProcedureComparisonQuery();

  // Check if market price data exists for the account and selected specialty
  const { data: availableCompetitors } = useGetAvailableCompetitors(
    filters.specialty ? { specialty: filters.specialty } : undefined
  );

  // Check if account has contract management chat bot configured and user is super admin
  const hasContractManagementChatBot = useMemo(() => {
    return (
      !!accountInfo?.contractManagementChatBotId &&
      userInfo?.scope === UserRole.SUPER_ADMIN
    );
  }, [accountInfo?.contractManagementChatBotId, userInfo?.scope]);

  // Check if market price data exists for the account and selected specialty
  const hasMarketPriceData = useMemo(() => {
    return availableCompetitors && availableCompetitors.length > 0;
  }, [availableCompetitors]);

  const {
    isLoading,
    isFetching,
    data: feeSchedulesFromAPI,
  } = useGetAllProviderProcedureComparisonQuery({
    accountId,
    page: first,
    pageSize: rows,
    ...filters,
  });
  const {
    data: top10ProviderProcedureComparison,
    isLoading: isTop10DataLoading,
    isFetching: isTop10DataFetching,
  } = useGetTop10ProviderProcedureComparisonQuery(
    {
      accountId,
      specialty: filters.specialty,
      limit: topResultsAmount,
    },
    {
      skip: !accountId,
    }
  );

  const {
    isLoading: isAllowableVsPtResponsibilityLoading,
    isFetching: isAllowableVsPtResponsibilityFetching,
    data: dataFromAPIAllowableVsPtResponsibility,
  } = useGetTotalAllowableAndPatientPortionQuery({
    page: firstAllowableVsPtResponsibility,
    pageSize: rowsAllowableVsPtResponsibility,
    accountId,
  });

  const [
    getContractInsightsRecommendations,
    { data: contractInsightsData, isLoading: isLoadingContractInsights },
  ] = useGetContractInsightsRecommendationsMutation();

  const {
    isLoading: isTopCasesWithPayersLoading,
    isFetching: isTopCasesWithPayersFetching,
    data: dataFromAPITopCasesWithPayers,
  } = useGetTopCaseRatesWithPayersQuery({
    pageSize: rowsTopCasesWithPayers,
    accountId,
  });

  const debouncedSetFilters = debounce(
    (
      updater: (
        prev: IProviderProcedureComparisonRequest
      ) => IProviderProcedureComparisonRequest
    ) => {
      setFilters((prev) => updater(prev));
    },
    500
  );

  useEffect(() => {
    if (!Object.keys(inputValues).length) {
      return undefined;
    }
    debouncedSetFilters((prevFilters: IProviderProcedureComparisonRequest) => ({
      ...prevFilters,
      ...inputValues,
    }));
    return () => {
      debouncedSetFilters.cancel();
    };
  }, [inputValues]);

  // Set default selected payers only on initial load or specialty change
  useEffect(() => {
    if (feeSchedulesFromAPI?.payers && !hasSetDefaultPayers.current) {
      const defaultPayerNames = [
        "Cigna PPO",
        "United Healthcare",
        "Medicare",
        "Aetna",
        "BC Select / Plus /Traditional",
      ];

      const defaultPayers = feeSchedulesFromAPI.payers.filter((payer) =>
        defaultPayerNames.includes(payer.name)
      );

      setSelectedPayers(defaultPayers);
      hasSetDefaultPayers.current = true;
    }
  }, [feeSchedulesFromAPI?.payers]);

  // Reset default payers flag when specialty changes
  useEffect(() => {
    hasSetDefaultPayers.current = false;
  }, [filters.specialty]);

  useEffect(() => {
    if (accountId && filters.specialty) {
      getContractInsightsRecommendations({
        accountId,
        specialty: filters.specialty,
      });
    }
  }, [accountId, filters.specialty, getContractInsightsRecommendations]);

  const feeSchedules = useMemo(() => {
    return feeSchedulesFromAPI?.data.map((feeSchedule) => {
      return {
        procedureId: feeSchedule.procedureId,
        cptCode: feeSchedule?.procedure?.cptCode,
        cptDescription: feeSchedule?.procedure?.description,
        provider: feeSchedule?.provider?.name,
        specialty: SpecialtyListDict[feeSchedule?.provider?.specialty],
        connectedHospital: feeSchedule?.provider?.account.name,
        payers: feeSchedule?.payers,
        volume: feeSchedule?.procedure.orderCount,
      };
    });
  }, [feeSchedulesFromAPI]);

  const handleDownloadData = async () => {
    try {
      setIsDownloading(true);
      const result = await triggerDownloadQuery({
        accountId,
        ...filters,
      }).unwrap();
      const fullData = result.data;
      const flattenedData = fullData.map((item) => {
        // Only include selected payers in the output, or all if none selected
        const payersToInclude =
          selectedPayers.length > 0
            ? item.payers?.filter((p) =>
                selectedPayers.some((sp) => sp.id === p.id)
              )
            : item.payers;

        const payerMap = Object.fromEntries(
          (payersToInclude ?? []).map((payer) => [
            payer.name,
            payer.priceInCents ? centsToDollars(payer.priceInCents) : null,
          ])
        );

        return {
          CPT_Code: item.procedure?.cptCode,
          Procedure: item.procedure?.description,
          Provider: item.provider?.name,
          Specialty: item.provider?.specialty,
          Hospital: item.provider?.account?.name,
          Volume: item.procedure?.orderCount,
          ...payerMap,
        };
      });

      await exportToExcel(flattenedData, "fee_schedule.csv");
    } catch (error) {
      console.error("Error downloading data:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const onPageChange = (event: PaginatorPageState) => {
    setFirst(event.first);
  };

  function payerColumn(row: any, payerId: number) {
    const payerPrice = row.payers.find(
      (payer: IPayerWithProcedurePrice) => payer.id === payerId
    );
    const isLowerThanMedicare = !!payerPrice?.lowerThanMedicare;
    const hasImplantCost =
      payerPrice?.implantCostInCents && payerPrice.implantCostInCents > 0;
    const totalWithImplant = hasImplantCost
      ? (payerPrice.priceInCents || 0) + payerPrice.implantCostInCents
      : null;

    const rateSource = feeRateSourceFromEffectiveDate(
      payerPrice?.contractFeeScheduleEffectiveDate
    );
    const mainPrice =
      payerPrice?.priceInCents != null
        ? parsePrice(centsToDollars(payerPrice.priceInCents))
        : "-";

    const renderPriceContent = () => (
      <div className="flex flex-column">
        <div
          className="flex flex-column gap-1"
          aria-label={
            mainPrice !== "-"
              ? `${mainPrice}, ${rateSource.ariaPriceModifier}`
              : rateSource.ariaPriceModifier
          }
        >
          <div className="flex gap-1 align-items-center">
            {mainPrice}
            {isLowerThanMedicare && (
              <i className="pi pi-caret-down text-red-500" />
            )}
          </div>
          <span className="text-xs text-gray-500" aria-hidden="true">
            {rateSource.caption}
          </span>
        </div>
        {hasImplantCost && (
          <>
            <Tooltip
              target={`.implant-tooltip-${row.procedureId}-${payerId}`}
            />
            <div
              className={`implant-tooltip-${row.procedureId}-${payerId} cursor-pointer`}
              data-pr-tooltip={`Implant Payment is ${parsePrice(
                centsToDollars(payerPrice.implantCostInCents)
              )}`}
              style={{
                fontSize: "0.75rem",
                fontStyle: "italic",
                color: "#6b7280",
                marginTop: "2px",
              }}
            >
              Total: {parsePrice(centsToDollars(totalWithImplant))}
            </div>
          </>
        )}
      </div>
    );

    if (!isLowerThanMedicare) {
      return renderPriceContent();
    }

    return (
      <>
        <Tooltip target={`.tooltip-${row.procedureId}-${payerId}`} />
        <div
          className={`cursor-pointer tooltip-${row.procedureId}-${payerId}`}
          data-pr-tooltip="Lower than Medicare"
        >
          {renderPriceContent()}
        </div>
      </>
    );
  }

  const genericInputFilterTemplate = (
    fieldName: string,
    filterName: string,
    placeholder: string
  ) => {
    const isCpt = filterName === "cptCodeField";
    return (
      <StyledInputTextContainer className={isCpt ? "lg:w-16rem" : "lg:w-20rem"}>
        <i className="pi pi-search" style={{ color: "#98A2B3" }} />
        <StyledInputText
          className="p-column-filter no-icon max-h-3rem"
          id={`${fieldName}-filter`}
          autoComplete="off"
          placeholder={placeholder}
          aria-labelledby="filter"
          value={inputValues[filterName] || ""}
          onChange={(e) => {
            setInputValues((prevState) => ({
              ...prevState,
              [filterName]: e.target.value,
            }));
          }}
        />
      </StyledInputTextContainer>
    );
  };

  const handleVolumeFilter = () => {
    const value = volumeFilter;
    const mode = matchMode;

    setFilters((prev) => ({
      ...prev,
      volume: value === null || value === undefined ? undefined : value,
      volumeMatchMode: mode || "equals",
    }));
  };

  const volumeFilterTemplate = () => {
    return (
      <div className="flex flex-column gap-2">
        <Dropdown
          value={matchMode}
          onChange={(e) => setMatchMode(e.value)}
          options={matchModeOptions}
          optionLabel="label"
          placeholder="Select a Match Mode"
          className="w-full"
        />
        <InputNumber
          value={volumeFilter}
          onChange={(e) => setVolumeFilter(e.value)}
          placeholder="Volume"
        />
        <div className="flex justify-content-between gap-2 mt-3">
          <Button
            label="Clear"
            icon="pi pi-times"
            className="p-button-outlined"
            onClick={() => {
              setVolumeFilter(null);
              setMatchMode("equals");
              setFilters((prev) => ({
                ...prev,
                volume: undefined,
                volumeMatchMode: "equals",
              }));
            }}
          />
          <Button
            label="Apply"
            icon="pi pi-check"
            onClick={() => handleVolumeFilter()}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-column gap-4" style={{ position: "relative" }}>
      {/* Contract Management Chat Widget */}
      {hasContractManagementChatBot && (
        <ChatWidget
          accountId={accountId}
          chatType="ContractManagementBot"
          position="fixed"
          title="Contract Management Assistant"
          welcomeMessage="👋 Hi! I'm your Contract Management Assistant. I can help you analyze contracts, understand pricing insights, and provide negotiation recommendations based on the data you're viewing. How can I assist you today?"
        />
      )}

      {isLoading ? (
        <div className="flex flex-column gap-3 align-items-center">
          <LoadingSpinner />
          <p className="text-gray-600 text-center">Generating AI Insights...</p>
        </div>
      ) : (
        <div className="flex flex-column gap-3">
          <div className="flex gap-2 align-items-center md:flex-row flex-column">
            <div className="flex gap-2 align-items-center">
              <Stars
                className="text-2xl text-primary"
                style={{ minWidth: "24px" }}
              />
              <p className="text-primary text-lg md:text-2xl font-semibold">
                Payer AI Insights
              </p>
            </div>
            <Dropdown
              value={filters.specialty}
              className="p-column-filter w-full md:w-16rem ml-4"
              placeholder="Provider Category"
              options={Object.entries(SpecialtyListDict).map(
                ([value, label]) => ({
                  value,
                  label,
                })
              )}
              onChange={(e) => {
                setInputValues((prevState) => ({
                  ...prevState,
                  specialty: e.target.value,
                }));
              }}
            />
          </div>
          <div className="flex flex-column gap-2">
            <Label htmlFor="top-payers" className="text-sm text-gray-new-700">
              Results for
            </Label>
            <div className="flex gap-3">
              <StyledTopCPTCodesTag
                active={topResultsAmount === 10}
                className="bg-white"
                onClick={() => setTopResultsAmount(10)}
              >
                Top 10 CPT Codes
              </StyledTopCPTCodesTag>
              <StyledTopCPTCodesTag
                active={topResultsAmount === 100}
                className="bg-white"
                onClick={() => setTopResultsAmount(100)}
              >
                Top 100 CPT Codes
              </StyledTopCPTCodesTag>
            </div>
          </div>
          {isTop10DataLoading ||
          isTop10DataFetching ||
          isAllowableVsPtResponsibilityLoading ||
          isAllowableVsPtResponsibilityFetching ? (
            <LoadingSpinner />
          ) : (
            <Insights
              top10Data={top10ProviderProcedureComparison?.data}
              topResultsAmount={topResultsAmount}
              dataFromAPIAllowableVsPtResponsibility={
                dataFromAPIAllowableVsPtResponsibility
              }
              hasMarketPriceData={hasMarketPriceData}
              contractInsightsData={contractInsightsData}
              isLoadingContractInsights={isLoadingContractInsights}
            />
          )}
          <StyledContainer className="bg-white">
            <div className="p-4 flex flex-column gap-2">
              <p className="text-md font-bold mb-2">All Payer Rates</p>
              <div className="flex lg:flex-row flex-column justify-content-between lg:align-items-center gap-3">
                <div className="flex lg:flex-row flex-column align-items-start gap-4">
                  <div className="flex flex-column gap-2">
                    <Label htmlFor="payers" className="text-sm text-gray-700">
                      Selected Payers To Show
                    </Label>
                    <MultiSelect
                      value={selectedPayers}
                      onChange={(e) => setSelectedPayers(e.value)}
                      options={
                        feeSchedulesFromAPI?.payers
                          ?.slice()
                          .sort((a, b) => a.name.localeCompare(b.name)) || []
                      }
                      optionLabel="name"
                      placeholder="Show Payers"
                      className="w-full md:w-16rem"
                      showClear
                      filter
                      filterTemplate={
                        <div className="pl-2">Show All Payers</div>
                      }
                      style={{
                        background:
                          selectedPayers?.length > 0 ? "#F9FAFB" : "white",
                        border: "1px solid #D0D5DD",
                        boxShadow:
                          selectedPayers?.length > 0
                            ? "0px 1px 2px rgba(16, 24, 40, 0.05)"
                            : "none",
                        color: "#475467",
                      }}
                    />
                  </div>
                  <div className="flex gap-3 flex-column">
                    <Label htmlFor="payers" className="text-sm text-gray-700">
                      Show Prices Lower than Medicare
                    </Label>
                    <InputSwitch
                      checked={filters.onlyLowerThanMedicare}
                      onChange={(e) =>
                        setInputValues((prevState) => ({
                          ...prevState,
                          onlyLowerThanMedicare: e.value,
                        }))
                      }
                    />
                  </div>
                </div>
                <Button
                  label="Download"
                  icon="pi pi-download"
                  onClick={handleDownloadData}
                  loading={isDownloading}
                  className="p-button-outlined max-h-3rem max-w-12rem"
                />
              </div>
              <HorizontalLine className="border-gray-new-200 " />
              <div className="flex gap-3 xl:flex-row flex-column align-items-start xl:align-items-end">
                <div className="flex gap-3 flex-column lg:flex-row ">
                  {genericInputFilterTemplate(
                    "CPT Code",
                    "cptCodeField",
                    "CPT Code"
                  )}
                  {genericInputFilterTemplate(
                    "Procedure",
                    "cptDescription",
                    "Search Procedure"
                  )}
                </div>
              </div>

              <StyledDataTable
                rowGroupMode="rowspan"
                groupRowsBy="procedureId"
                rows={rows}
                rowHover
                stripedRows
                dataKey="id"
                editMode="cell"
                className="p-fluid datatable__FeeScheduleComparison mt-4"
                value={feeSchedules}
                loading={isFetching}
                responsiveLayout="scroll"
                emptyMessage="No fee schedule to show."
                volumeFilterActive={
                  filters.volume !== undefined && filters.volume !== null
                }
              >
                <Column
                  field="cptCode"
                  header="CPT Code"
                  style={{ minWidth: "150px" }}
                />
                <Column
                  field="cptDescription"
                  header="Procedure"
                  style={{ minWidth: "260px" }}
                />
                <Column
                  field="volume"
                  header="Volume"
                  style={{ minWidth: "100px" }}
                  filter
                  filterElement={volumeFilterTemplate}
                  showFilterOperator={false}
                  showAddButton={false}
                  showApplyButton={false}
                  showClearButton={false}
                  showFilterMatchModes={false}
                  onFilterApplyClick={handleVolumeFilter}
                  filterApply={false}
                  filterFunction={() => true}
                  dataType="numeric"
                />
                {selectedPayers?.map((payer) => (
                  <Column
                    key={payer.name}
                    field={`${payer}_price`}
                    header={payer?.name}
                    style={{ minWidth: "120px" }}
                    body={(rowData) => payerColumn(rowData, payer.id)}
                  />
                ))}
                {/* <Column
                  field="provider"
                  style={{ minWidth: "200px" }}
                  header="Provider/Hospital Name"
                /> */}
              </StyledDataTable>
              <Paginator
                first={first}
                rows={rows}
                totalRecords={feeSchedulesFromAPI?.total}
                onPageChange={onPageChange}
              />
            </div>
          </StyledContainer>
        </div>
      )}
      {hasMarketPriceData && (
        <MarketPriceAnalysis
          accountId={accountId}
          specialty={filters.specialty}
        />
      )}
      <ContractModelingAnalysis specialty={filters.specialty} />
      <AllowableVsPtResponsibility
        first={firstAllowableVsPtResponsibility}
        setFirst={setFirstAllowableVsPtResponsibility}
        dataFromAPI={dataFromAPIAllowableVsPtResponsibility}
        isLoading={isAllowableVsPtResponsibilityLoading}
        isFetching={isAllowableVsPtResponsibilityFetching}
      />
      <TopCasesWithPayers
        dataFromAPI={dataFromAPITopCasesWithPayers}
        isLoading={isTopCasesWithPayersLoading}
        isFetching={isTopCasesWithPayersFetching}
        payers={feeSchedulesFromAPI?.payers || []}
      />
    </div>
  );
}
